import { useState, useMemo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    TableSortLabel,
  } from '@mui/material';
  import { useDateSort } from '../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../components/common/TableActionsMenu';
  import { AdcashGuruNAMMetrics } from '../../../../types/adcash.types';
  
  type Props = {
    data: AdcashGuruNAMMetrics;
    exchangeRate: number;
    onExchangeRateChange: (rate: number) => void;
  };
  
  export default function AdcashGuruNAMMetricsTable({ data, exchangeRate, onExchangeRateChange }: Props) {
    const dailyStats = data.dailyStats;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    // 같은 날짜끼리 합산
    const aggregatedDataRaw = useMemo(() => {
      const grouped = dailyStats.reduce((acc, stat) => {
        const existing = acc.find(item => item.date === stat.date);
        if (existing) {
          existing.revenue += stat.revenue;
          existing.req += stat.req;
          existing.res += stat.res;
          existing.imp += stat.imp;
          existing.clk += stat.clk;
        } else {
          acc.push({
            date: stat.date,
            appName: stat.appName,
            revenue: stat.revenue,
            req: stat.req,
            res: stat.res,
            imp: stat.imp,
            clk: stat.clk
          });
        }
        return acc;
      }, [] as typeof dailyStats);
      
      return grouped;
    }, [dailyStats]);

    const { sorted: aggregatedData, sortDirection, toggleSort } = useDateSort(aggregatedDataRaw);

    const paged = useMemo(
        () => aggregatedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [aggregatedData, page]
    );

    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    }, []);

    const calculateFillRate = (req: number, imp: number) => {
        return req > 0 ? ((imp / req) * 100).toFixed(2) : '0.00';
    };

    const calculateCTR = (imp: number, clk: number) => {
        return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
    };

    const formatNumberOnly = useCallback((value: number) => Math.round(value).toLocaleString('ko-KR'), []);

    const calculateECPM = useCallback((revenue: number, imp: number) => {
        return imp > 0 ? formatNumberOnly((revenue / imp) * 1000) : '0';
    }, [formatNumberOnly]);

    // 합계 계산
    const totals = useMemo(() => {
        const totalRevenue = aggregatedData.reduce((sum, row) => sum + row.revenue, 0) * exchangeRate;
        const totalReq = aggregatedData.reduce((sum, row) => sum + row.req, 0);
        const totalRes = aggregatedData.reduce((sum, row) => sum + row.res, 0);
        const totalImp = aggregatedData.reduce((sum, row) => sum + row.imp, 0);
        const totalClk = aggregatedData.reduce((sum, row) => sum + row.clk, 0);
        
        const avgFillRate = totalReq > 0 ? ((totalImp / totalReq) * 100).toFixed(2) : '0.00';
        const avgCTR = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
        const avgECPM = totalImp > 0 ? formatCurrency((totalRevenue / totalImp) * 1000) : formatCurrency(0);
        
        // 전달 대비 비교
        const prevRevenue = (data.prevTotalRevenue || 0) * exchangeRate;
        const revenueDelta = totalRevenue - prevRevenue;
        const revenueDeltaPercentage = prevRevenue > 0 
            ? ((revenueDelta / prevRevenue) * 100).toFixed(0)
            : '0';
        const isPositiveRevenue = revenueDelta >= 0;

        const prevFillRate = data.prevTotalReq > 0 ? ((data.prevTotalImp / data.prevTotalReq) * 100).toFixed(2) : '0.00';
        const fillRateDelta = (Number(avgFillRate) - Number(prevFillRate)).toFixed(2);
        const isPositiveFillRate = Number(fillRateDelta) >= 0;
        
        return {
            totalRevenue,
            totalReq,
            totalRes,
            totalImp,
            totalClk,
            avgFillRate,
            avgCTR,
            avgECPM,
            revenueDelta,
            revenueDeltaPercentage,
            isPositiveRevenue,
            prevFillRate,
            fillRateDelta,
            isPositiveFillRate
        };
    }, [aggregatedData, exchangeRate, data.prevTotalRevenue, data.prevTotalReq, data.prevTotalImp, formatCurrency]);

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시, 합계 행 제외)
    // 퍼센트 컬럼은 소수로 전달
    const allRowsDataForExcel = useMemo(() => {
        return aggregatedData.map((row) => {
            // fillRate와 CTR을 소수 형태로 (0.xx)
            const fillRate = row.req > 0 ? row.imp / row.req : 0;
            const ctr = row.imp > 0 ? row.clk / row.imp : 0;
            const ecpm = calculateECPM(row.revenue * exchangeRate, row.imp);
            return [
                row.date,
                formatNumberOnly(row.revenue * exchangeRate),
                row.req.toLocaleString(),
                row.res.toLocaleString(),
                fillRate,
                row.imp.toLocaleString(),
                row.clk.toLocaleString(),
                ctr,
                ecpm
            ];
        });
    }, [aggregatedData, exchangeRate, formatNumberOnly, calculateECPM]);

    return (
        <Box>
            {/* 환율 버튼 1230 or 1350 */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {[1230, 1350].map(rate => (
                        <Button
                            key={rate}
                            size="small"
                            variant="outlined"
                            onClick={() => onExchangeRateChange(rate)}
                            sx={{
                                borderColor: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                                color: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: exchangeRate === rate ? '#0044D2' : '#757575',
                                    backgroundColor: exchangeRate === rate ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                                }
                            }}
                        >
                            환율 {rate}
                        </Button>
                    ))}
                </Box>
                <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto'
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>총 매출</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Request</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Response</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Fill Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Impression</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Click</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>CTR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>eCPM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Box>
                                {formatCurrency(totals.totalRevenue)}
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: totals.isPositiveRevenue ? '#D32F2F' : '#1976D2',
                                        ml: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    ({totals.isPositiveRevenue ? '▲' : '▼'} {totals.revenueDeltaPercentage}%)
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalReq.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalRes.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Box>
                                {totals.avgFillRate}%
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: totals.isPositiveFillRate ? '#D32F2F' : '#1976D2',
                                        ml: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    ({totals.isPositiveFillRate ? '▲' : '▼'} {totals.fillRateDelta}%)
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalImp.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalClk.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.avgCTR}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.avgECPM}</TableCell>
                    </TableRow>
                    {paged.map((row) => {
                        const fillRate = calculateFillRate(row.req, row.imp);
                        const ctr = calculateCTR(row.imp, row.clk);
                        const ecpm = calculateECPM(row.revenue * exchangeRate, row.imp);

                        return (
                            <TableRow key={row.date}>
                                <TableCell>{row.date}</TableCell>
                                <TableCell align="right">{formatCurrency(row.revenue * exchangeRate)}</TableCell>
                                <TableCell align="right">{row.req.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.res.toLocaleString()}</TableCell>
                                <TableCell align="right">{fillRate}%</TableCell>
                                <TableCell align="right">{row.imp.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.clk.toLocaleString()}</TableCell>
                                <TableCell align="right">{ctr}%</TableCell>
                                <TableCell align="right">{ecpm}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={aggregatedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }
  